import type { OverviewResponse } from "contracts/api/dashboard/responses";
import type { PageMapLens } from "../../enums/map";
import type {
  EstateBand,
  EstateClusterEdge,
  EstateEdge,
  EstateNode,
  Page,
} from "./estateTypes";
import { edgeRank, touches } from "./estateViewport";
import { clusterContextForPages, clusterKey } from "./scanModel";

export function groupedPages(pages: Page[]): Map<string, Page[]> {
  const clusterContext = clusterContextForPages(pages);
  const groups = new Map<string, Page[]>();
  for (const page of pages) {
    const key = clusterKey(page.path, clusterContext);
    groups.set(key, [...(groups.get(key) ?? []), page]);
  }
  return groups;
}

export function buildEdges(
  data: OverviewResponse,
  nodes: EstateNode[],
): EstateEdge[] {
  const nodeById = new Map(nodes.map((node) => [node.page.id, node]));
  const seen = new Set<string>();
  const edges: EstateEdge[] = [];
  for (const link of data.links) {
    if (link.fromPageId === link.toPageId) continue;
    const from = nodeById.get(link.fromPageId);
    const to = nodeById.get(link.toPageId);
    if (!from || !to) continue;
    const key = estateEdgeKey(link.fromPageId, link.toPageId);
    if (seen.has(key)) continue;
    seen.add(key);
    edges.push({ key, link, from, to });
  }
  return edges;
}

export function estateEdgeKey(fromPageId: string, toPageId: string): string {
  return [fromPageId, toPageId].sort().join(":");
}

export function includeFocusedEdges(
  visible: EstateEdge[],
  all: EstateEdge[],
  focusedKeys: Set<string>,
): EstateEdge[] {
  if (focusedKeys.size === 0) return visible;
  const focused = all.filter((edge) => focusedKeys.has(edge.key));
  const focusedEdgeKeys = new Set(focused.map((edge) => edge.key));
  return [
    ...visible.filter((edge) => !focusedEdgeKeys.has(edge.key)),
    ...focused,
  ];
}

export function buildClusterEdges(
  data: OverviewResponse,
  bands: EstateBand[],
): EstateClusterEdge[] {
  const bandByKey = new Map(bands.map((band) => [band.key, band]));
  const clusterContext = clusterContextForPages(data.pages);
  const clusterByPageId = new Map(
    data.pages.map((page) => [page.id, clusterKey(page.path, clusterContext)]),
  );
  const pagePairs = new Set<string>();
  const aggregate = new Map<
    string,
    { from: EstateBand; to: EstateBand; count: number; brokenCount: number }
  >();

  for (const link of data.links) {
    if (link.fromPageId === link.toPageId) continue;
    const pagePair = estateEdgeKey(link.fromPageId, link.toPageId);
    if (pagePairs.has(pagePair)) continue;
    pagePairs.add(pagePair);
    const fromKey = clusterByPageId.get(link.fromPageId);
    const toKey = clusterByPageId.get(link.toPageId);
    if (!fromKey || !toKey || fromKey === toKey) continue;
    const [firstKey, secondKey] = [fromKey, toKey].sort();
    const from = bandByKey.get(firstKey);
    const to = bandByKey.get(secondKey);
    if (!from || !to) continue;
    const key = `${firstKey}:${secondKey}`;
    const current = aggregate.get(key) ?? {
      from,
      to,
      count: 0,
      brokenCount: 0,
    };
    current.count += 1;
    current.brokenCount += link.agentBroken ? 1 : 0;
    aggregate.set(key, current);
  }

  return [...aggregate.entries()].map(([key, edge]) => ({ key, ...edge }));
}

export function selectVisibleEdges(
  edges: EstateEdge[],
  contextPageId: string | null,
  limit: number,
  lens: PageMapLens,
): EstateEdge[] {
  const ranked = [...edges].sort(
    (left, right) =>
      edgeRank(right, contextPageId, lens) -
        edgeRank(left, contextPageId, lens) ||
      left.key.localeCompare(right.key),
  );
  if (contextPageId) {
    return ranked
      .filter((edge) => touches(edge, contextPageId))
      .slice(0, limit);
  }
  const selected = new Map<string, EstateEdge>();
  for (const edge of ranked.filter((item) => item.link.agentBroken)) {
    selected.set(edge.key, edge);
    if (selected.size >= limit) return [...selected.values()];
  }
  for (const edge of ranked) {
    selected.set(edge.key, edge);
    if (selected.size >= limit) break;
  }
  return [...selected.values()];
}

export function connectedPageIds(
  edges: EstateEdge[],
  contextPageId: string | null,
): Set<string> {
  const ids = new Set<string>();
  if (!contextPageId) return ids;
  ids.add(contextPageId);
  for (const edge of edges) {
    if (edge.from.page.id === contextPageId) ids.add(edge.to.page.id);
    if (edge.to.page.id === contextPageId) ids.add(edge.from.page.id);
  }
  return ids;
}

export function structuralConnectionsForPage(
  data: OverviewResponse,
  pageId: string,
): { inbound: Page[]; outbound: Page[]; connected: Page[] } {
  const byId = new Map(data.pages.map((page) => [page.id, page]));
  const inboundIds = new Set<string>();
  const outboundIds = new Set<string>();
  for (const link of data.links) {
    if (link.fromPageId === pageId && link.toPageId !== pageId) {
      outboundIds.add(link.toPageId);
    }
    if (link.toPageId === pageId && link.fromPageId !== pageId) {
      inboundIds.add(link.fromPageId);
    }
  }
  const toPages = (ids: Set<string>) =>
    [...ids]
      .map((id) => byId.get(id))
      .filter((page): page is Page => Boolean(page));
  const inbound = toPages(inboundIds);
  const outbound = toPages(outboundIds);
  return {
    inbound,
    outbound,
    connected: toPages(new Set([...inboundIds, ...outboundIds])),
  };
}
